/* eslint-disable camelcase */
import { z } from 'zod'

import {
  channelRules,
  labelRuleLabels,
  labelSourceLabels,
  labelSourceOrder,
  llmRoutes,
  selectedLabelSource,
  validLabelEvaluation,
} from './labelSources'
import {
  dimensionLabels,
  dimensionValueLabel,
  formatCount,
  formatDuration,
  formatPercent,
  percentage,
  validDimensionValue,
} from './model'

import type {
  AttributionLabelRule,
  AttributionLabelSource,
  AttributionSourceSelection,
} from '@/api/attributionLabelSources'
import type { AttributionDimension, AttributionFilters, AttributionSummary } from '@/api/attributionObservability'

export const metrics = [
  'labels',
  'labels_by_words',
  'fully_named',
  'summary_speaker_chips_named',
  'ax',
  'ax_capture',
  'meetings',
] as const
export type Metric = (typeof metrics)[number]
export const metricLabels: Record<Metric, string> = {
  labels: 'Speaker naming coverage',
  labels_by_words: 'Speaker label %, weighted by words',
  fully_named: 'Fully named meetings',
  summary_speaker_chips_named: 'Summary speaker chips named',
  ax: 'AX evidence coverage',
  ax_capture: 'AX capture coverage',
  meetings: 'Meetings',
}

export const metricPhrase = (metric: Metric) =>
  metric === 'ax' || metric === 'ax_capture' ? metricLabels[metric] : metricLabels[metric].toLowerCase()

export const meetingSplitDimensions = [
  'platform',
  'os',
  'participant_bucket',
  'user_meeting_bucket',
  'calendar_event',
  'ax_bucket',
  'recording_length_bucket',
  'client',
  'backend_version',
  'app_version',
] as const satisfies readonly AttributionDimension[]
export type MeetingSplitDimension = (typeof meetingSplitDimensions)[number]
export const splitDimensions = [...meetingSplitDimensions, 'label_source', 'channel_rule', 'llm_route'] as const
export type SplitDimension = (typeof splitDimensions)[number]
export type CohortStep =
  | [MeetingSplitDimension, string]
  | ['label_source', AttributionLabelSource]
  | ['channel_rule', (typeof channelRules)[number]]
  | ['llm_route', (typeof llmRoutes)[number]]
export type CohortPath = CohortStep[]
export type TreeSplits = Record<string, SplitDimension | null>
export const MAX_DEPTH = 6
export const MAX_SPLITS = 32
const MAX_TREE_LENGTH = 12_000

const dimensionSchema = z.enum(splitDimensions)
const pathSchema = z
  .array(
    z.union([
      z.tuple([z.enum(meetingSplitDimensions), z.string().max(64)]),
      z.tuple([z.literal('label_source'), z.enum(labelSourceOrder)]),
      z.tuple([z.literal('channel_rule'), z.enum(channelRules)]),
      z.tuple([z.literal('llm_route'), z.enum(llmRoutes)]),
    ])
  )
  .max(MAX_DEPTH)
const splitsSchema = z
  .record(z.string(), dimensionSchema.nullable())
  .refine((value) => Object.keys(value).length <= MAX_SPLITS)

export const isMeetingSplitDimension = (dimension: SplitDimension): dimension is MeetingSplitDimension =>
  (meetingSplitDimensions as readonly string[]).includes(dimension)
export const splitLabel = (dimension: SplitDimension) =>
  isMeetingSplitDimension(dimension)
    ? dimensionLabels[dimension]
    : dimension === 'label_source'
      ? 'Speaker label source'
      : dimension === 'channel_rule'
        ? 'Channel rule'
        : 'LLM route'
export const cohortValueLabel = (dimension: SplitDimension, value: string) =>
  isMeetingSplitDimension(dimension)
    ? dimensionValueLabel(dimension, value)
    : dimension === 'label_source'
      ? labelSourceLabels[value as AttributionLabelSource]
      : labelRuleLabels[value as AttributionLabelRule]
export const pathKey = (path: CohortPath) => JSON.stringify(path)
export const sourceSelectionForPath = (path: CohortPath): AttributionSourceSelection | undefined => {
  const source = path.find((step) => step[0] === 'label_source')
  if (!source || source[0] !== 'label_source') return undefined
  const rule = path.find((step) => step[0] === 'channel_rule' || step[0] === 'llm_route')
  return {
    label_source: source[1],
    ...(rule ? { label_rule: rule[1] as AttributionLabelRule } : {}),
  }
}

export const filtersForPath = (filters: AttributionFilters, path: CohortPath): AttributionFilters => {
  const physical = path.filter(([dimension]) => isMeetingSplitDimension(dimension))
  const scoped = { ...filters, ...Object.fromEntries(physical) }
  const negate = filters.negate?.filter((dimension) => !physical.some(([key]) => key === dimension))
  if (negate?.length) scoped.negate = negate
  else delete scoped.negate
  return scoped
}

export const availableSplits = (filters: AttributionFilters, path: CohortPath): SplitDimension[] => {
  if (path.length >= MAX_DEPTH) return []
  const selection = sourceSelectionForPath(path)
  return splitDimensions.filter((dimension) => {
    if (path.some(([key]) => key === dimension)) return false
    if (isMeetingSplitDimension(dimension)) return !filters[dimension] || !!filters.negate?.includes(dimension)
    if (dimension === 'label_source') return !selection
    return !selection?.label_rule && selection?.label_source === (dimension === 'channel_rule' ? 'channel' : 'llm')
  })
}

export const readMetric = (search: URLSearchParams): Metric => {
  const value = search.get('metric')
  return metrics.find((metric) => metric === value) ?? 'labels_by_words'
}

const validPath = (path: CohortPath, filters: AttributionFilters) =>
  new Set(path.map(([dimension]) => dimension)).size === path.length &&
  path.every(([dimension, value], index) => {
    if (!availableSplits(filters, path.slice(0, index)).includes(dimension)) return false
    return (
      !isMeetingSplitDimension(dimension) ||
      (validDimensionValue(dimension, value) &&
        (!filters[dimension] || (!!filters.negate?.includes(dimension) && filters[dimension] !== value)))
    )
  })

export const readTreeSplits = (search: URLSearchParams, filters: AttributionFilters): TreeSplits => {
  const raw = search.get('tree')
  if (raw && raw.length <= MAX_TREE_LENGTH) {
    try {
      const result = splitsSchema.parse(JSON.parse(raw))
      for (const [key, dimension] of Object.entries(result)) {
        const path = pathSchema.parse(JSON.parse(key))
        if (!validPath(path, filters) || (dimension !== null && !availableSplits(filters, path).includes(dimension))) {
          throw new Error('Invalid exploration path')
        }
        for (let depth = 0; depth < path.length; depth += 1) {
          if (result[pathKey(path.slice(0, depth))] !== path[depth][0]) throw new Error('Disconnected exploration path')
        }
      }
      return result
    } catch {
      // Invalid shared URLs fall back to a bounded initial view.
    }
  }
  const choices = availableSplits(filters, [])
  return { [pathKey([])]: choices[0] ?? null }
}

export const treeUrlNotice = (search: URLSearchParams, filters: AttributionFilters): string | null => {
  const raw = search.get('tree')
  if (raw) {
    try {
      if (JSON.stringify(JSON.parse(raw)) !== JSON.stringify(readTreeSplits(search, filters)))
        return 'This saved view uses a split that is no longer available.'
    } catch {
      return 'This saved view has an invalid tree.'
    }
  }
  if (search.has('metric') && !metrics.some((metric) => metric === search.get('metric')))
    return 'This saved view uses a metric that is no longer available.'
  return null
}

export const isPathInTree = (path: CohortPath, splits: TreeSplits) =>
  path.every(([dimension], depth) => splits[pathKey(path.slice(0, depth))] === dimension)

const startsWithPath = (candidate: CohortPath, path: CohortPath) =>
  candidate.length >= path.length &&
  path.every(([dimension, value], index) => candidate[index][0] === dimension && candidate[index][1] === value)

export const changeSplit = (splits: TreeSplits, path: CohortPath, dimension: SplitDimension | null): TreeSplits => {
  const key = pathKey(path)
  if (splits[key] === dimension) return splits
  const next = Object.fromEntries(
    Object.entries(splits).filter(([stored]) => !startsWithPath(JSON.parse(stored), path))
  )
  return dimension === null && path.length > 0 ? next : { ...next, [key]: dimension }
}

export const metricValue = (summary: AttributionSummary, metric: Metric, filters?: AttributionFilters) => {
  if (metric === 'summary_speaker_chips_named')
    return {
      value: 'Not measured',
      detail: 'At summary publication; actual meeting speakers only.',
      measured: 0,
      percent: null,
    }
  if (metric === 'meetings')
    return {
      value: formatCount(summary.meeting_count),
      detail: 'Collected completed meetings',
      measured: summary.meeting_count,
      percent: null,
    }
  const [numerator, denominator, measured] =
    metric === 'labels'
      ? [summary.machine_labeled_speakers, summary.active_speakers, summary.measured_label_meetings]
      : metric === 'labels_by_words'
        ? [summary.machine_labeled_words, summary.words, summary.measured_word_meetings]
        : metric === 'fully_named'
          ? [summary.fully_machine_labeled_meetings, summary.measured_label_meetings, summary.measured_label_meetings]
          : metric === 'ax_capture'
            ? [summary.ax_readable_ms, summary.ax_capture_ms, summary.measured_ax_capture_meetings]
            : [summary.ax_covered_speech_ms, summary.speech_ms, summary.measured_ax_meetings]
  const notApplicable =
    metric === 'ax' && filters?.ax_bucket === 'not_applicable' && !filters.negate?.includes('ax_bucket')
  return {
    value: notApplicable ? 'Not applicable' : formatPercent(numerator, denominator),
    detail:
      metric === 'labels'
        ? `${formatCount(numerator)} / ${formatCount(denominator)} speakers`
        : metric === 'labels_by_words'
          ? `${formatCount(numerator)} / ${formatCount(denominator)} words`
          : metric === 'fully_named'
            ? `${formatCount(numerator)} / ${formatCount(denominator)} meetings`
            : `${formatDuration(numerator)} / ${formatDuration(denominator)}`,
    measured,
    percent: notApplicable ? null : percentage(numerator, denominator),
  }
}

export const focusTreeOnPath = (splits: TreeSplits, path: CohortPath, nextFilters: AttributionFilters): TreeSplits => {
  const anchor = path.filter(([dimension]) => !isMeetingSplitDimension(dimension))
  const next: TreeSplits = {}
  anchor.forEach(([dimension], index) => {
    next[pathKey(anchor.slice(0, index))] = dimension
  })
  next[pathKey(anchor)] = null
  const entries = Object.entries(splits).sort(([left], [right]) => JSON.parse(left).length - JSON.parse(right).length)
  for (const [key, dimension] of entries) {
    const candidate = JSON.parse(key) as CohortPath
    if (!startsWithPath(candidate, path)) continue
    const rebased = [...anchor, ...candidate.slice(path.length)]
    if (!validPath(rebased, nextFilters) || !isPathInTree(rebased, next)) continue
    if (dimension === null || availableSplits(nextFilters, rebased).includes(dimension))
      next[pathKey(rebased)] = dimension
  }
  return next
}

export const rowMeasurement = (
  summary: AttributionSummary,
  metric: Metric,
  filters: AttributionFilters,
  path: CohortPath
) => {
  const selection = sourceSelectionForPath(path)
  if (!selection)
    return {
      ...metricValue(summary, metric, filters),
      identity: metric as string,
      cue: null as string | null,
      meetings: summary.meeting_count,
      sourceMeasured: null as number | null,
      coverageHelp: null as string | null,
      claim: null,
    }
  const label = labelSourceLabels[selection.label_source]
  const showClaim = selection.label_source === 'llm' && !selection.label_rule
  const evaluation = showClaim ? validLabelEvaluation(summary, 'llm') : null
  const source = selectedLabelSource(summary, selection)
  const percent = source ? percentage(source.speakers, source.denominator) : null
  return {
    value: source ? formatPercent(source.speakers, source.denominator) : 'Unavailable',
    detail: source
      ? `${formatCount(source.speakers)} / ${formatCount(source.denominator)} ${selection.label_rule ? `${label} labels` : 'active speakers'}`
      : 'Source breakdown unavailable',
    percent,
    measured: source?.measured ?? 0,
    sourceMeasured: source?.measured ?? null,
    coverageHelp:
      'Meetings with usable final source assignments. This measures data completeness, not successful claims.',
    identity: `final:${selection.label_source}:${selection.label_rule ?? ''}`,
    cue: selection.label_rule
      ? `Final ${label} labels`
      : `Final ${selection.label_source === 'unnamed' ? 'unnamed' : label} share`,
    meetings: source?.meetings ?? null,
    claim: showClaim
      ? {
          value: !evaluation?.measured_meetings
            ? 'Not measured'
            : !evaluation.evaluated_speakers
              ? 'Not evaluated'
              : formatPercent(evaluation.claimed_speakers, evaluation.evaluated_speakers),
          detail: !evaluation?.measured_meetings
            ? 'Claim measurements unavailable'
            : `${formatCount(evaluation.claimed_speakers)} / ${formatCount(evaluation.evaluated_speakers)} evaluated speakers`,
          coverageHelp: evaluation
            ? `${formatCount(evaluation.evaluated_meetings)} evaluated meetings; ${formatCount(evaluation.measured_meetings)} / ${formatCount(summary.meeting_count)} meetings with LLM evaluation data. Claims from this attempt can differ from final assignments.`
            : 'LLM evaluation measurements unavailable.',
        }
      : null,
  }
}
